import React, { useEffect, useState } from "react";
import { Crown, Sparkle } from "@phosphor-icons/react";
import { api } from "../lib/api";

const DAY_NAMES_SHORT = { Lundi: "Lun", Mardi: "Mar", Mercredi: "Mer", Jeudi: "Jeu", Vendredi: "Ven", Samedi: "Sam", Dimanche: "Dim" };

/**
 * Insights panel: fetches Best Hours (Premium), Monopoly Local score (all tiers),
 * and Demand Forecast (Premium IA only). Renders 1-3 cards side by side.
 */
export default function InsightsPanel({ slug, isPremium, tier }) {
  const [hours, setHours] = useState(null);
  const [mono, setMono] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(false);

  const isPremiumAI = tier === "premium_ai";

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    Promise.allSettled([
      isPremium ? api.get(`/dashboard/insights/${slug}/best-hours`).then((r) => r.data) : Promise.resolve(null),
      api.get(`/dashboard/monopoly/${slug}`).then((r) => r.data),
      isPremiumAI ? api.get(`/dashboard/insights/${slug}/demand-forecast`).then((r) => r.data) : Promise.resolve(null),
    ]).then(([h, m, f]) => {
      setHours(h.status === "fulfilled" ? h.value : null);
      setMono(m.status === "fulfilled" ? m.value : null);
      setForecast(f.status === "fulfilled" ? f.value : null);
      setLoading(false);
    });
  }, [slug, isPremium, isPremiumAI]);

  if (loading) return null;
  if (!mono && !hours && !forecast) return null;

  const maxHour = hours?.hourly_distribution ? Math.max(...hours.hourly_distribution.map((h) => h.count)) || 1 : 1;
  const maxDay = hours?.weekly_distribution ? Math.max(...hours.weekly_distribution.map((d) => d.count)) || 1 : 1;

  return (
    <div className="grid md:grid-cols-2 gap-6 mt-8" data-testid="insights-panel">
      {/* Best hours */}
      {isPremium ? (
        hours && (
          <div className="bg-card border border-border rounded-sm p-6 lift-card-plus" data-testid="best-hours-card">
            <div className="flex items-start justify-between mb-5">
              <div>
                <div className="font-display text-lg font-medium">Meilleures heures</div>
                <div className="text-xs text-muted-foreground">Analyse 60 jours · IA</div>
              </div>
              <div className="inline-flex items-center gap-1 bg-accent text-accent-foreground text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm"><Sparkle size={10} weight="fill"/> IA</div>
            </div>
            <p className="text-sm leading-relaxed text-foreground/80 mb-4" data-testid="best-hours-advice">{hours.advice}</p>
            {hours.total_events > 0 && (
              <>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Par heure</div>
                <div className="flex items-end gap-0.5 h-20 mb-4">
                  {hours.hourly_distribution.map((h) => (
                    <div key={h.hour} className="flex-1 flex flex-col items-center justify-end" title={`${h.hour}h: ${h.count}`}>
                      <div className="w-full bg-primary/70 rounded-t-sm" style={{ height: `${(h.count / maxHour) * 100}%`, minHeight: h.count ? "2px" : "0" }} />
                    </div>
                  ))}
                </div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Par jour</div>
                <div className="grid grid-cols-7 gap-1">
                  {hours.weekly_distribution.map((d) => (
                    <div key={d.day} className="text-center">
                      <div className="h-12 flex items-end justify-center">
                        <div className="w-full bg-secondary/70 rounded-t-sm" style={{ height: `${(d.count / maxDay) * 100}%`, minHeight: d.count ? "2px" : "0" }} />
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-1">{DAY_NAMES_SHORT[d.day] || d.day.slice(0, 3)}</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )
      ) : (
        <div className="bg-card border border-border rounded-sm p-6 flex flex-col justify-between" data-testid="best-hours-locked">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-secondary/15 text-secondary text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-sm mb-3">
              <Crown size={10} weight="fill"/> Premium
            </div>
            <div className="font-display text-lg font-medium mb-1">Meilleures heures</div>
            <p className="text-sm text-muted-foreground">Découvrez les heures et jours où vos clients vous cherchent le plus — et publiez au bon moment.</p>
          </div>
          <a href="/passez-partenaire" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-secondary">
            Débloquer <Sparkle size={12} weight="fill"/>
          </a>
        </div>
      )}

      {/* Monopoly Local AI */}
      {mono && (
        <div className="bg-card border border-border rounded-sm p-6 lift-card-plus" data-testid="monopoly-card">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="font-display text-lg font-medium">Score Monopoly Local</div>
              <div className="text-xs text-muted-foreground">Votre position dans le 68 · IA</div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-medium tabular-nums text-primary" data-testid="monopoly-overall">{mono.overall}</div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">/ 100</div>
            </div>
          </div>
          <div className="space-y-2.5 mb-5">
            {Object.entries({
              reflex_local: "Réflexe local",
              notoriety: "Notoriété",
              costly_absence: "Absence coûteuse",
              ecosystem: "Écosystème",
              normalization: "Complétude fiche",
            }).map(([k, label]) => (
              <div key={k} data-testid={`monopoly-score-${k}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-muted-foreground">{label}</span>
                  <span className="tabular-nums font-bold">{mono.scores[k]}</span>
                </div>
                <div className="h-1.5 bg-accent rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-primary to-secondary rounded-full" style={{ width: `${mono.scores[k]}%` }} />
                </div>
              </div>
            ))}
          </div>
          {mono.insights.length > 0 && (
            <div className="pt-4 border-t border-border">
              <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Conseils IA</div>
              <ul className="text-sm space-y-1.5 list-disc pl-4">
                {mono.insights.map((ins, i) => (
                  <li key={`${i}-${ins.slice(0, 24)}`} className="text-foreground/80 leading-relaxed">{ins}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Demand forecast (Premium IA only) */}
      {isPremiumAI && forecast && forecast.ready && (
        <div className="bg-card border border-border rounded-sm p-6 lift-card-plus md:col-span-2" data-testid="demand-forecast-card">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="font-display text-lg font-medium">Prévision de demande — 7 jours</div>
              <div className="text-xs text-muted-foreground">Basée sur 90 j d'activité · projection IA</div>
            </div>
            <div className="inline-flex items-center gap-1 bg-secondary/15 text-secondary text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-sm">
              <Sparkle size={10} weight="fill"/> Premium IA
            </div>
          </div>
          <p className="text-sm leading-relaxed text-foreground/80 mb-5" data-testid="forecast-overall">{forecast.overall_advice}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="forecast-grid">
            {forecast.forecast.map((d) => {
              const color = d.level === "high" ? "bg-primary text-primary-foreground border-primary" : d.level === "low" ? "bg-accent/60 text-muted-foreground border-border" : "bg-card text-foreground border-border";
              const icon = d.level === "high" ? "▲" : d.level === "low" ? "▼" : "◆";
              return (
                <div key={d.date} className={`border rounded-sm p-3 flex flex-col gap-1 ${color}`} data-testid={`forecast-day-${d.dow}`}>
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-80">{d.day_name.slice(0, 3)}</div>
                  <div className="font-display text-xl font-medium tabular-nums leading-none">{d.expected_events}</div>
                  <div className="text-[10px] opacity-70">{icon} {d.level === "high" ? "Fort" : d.level === "low" ? "Creux" : "Moyen"}</div>
                  {d.tip && <div className="text-[11px] leading-snug mt-1 opacity-90">{d.tip}</div>}
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-muted-foreground mt-3">
            Tendance 30 j vs 60 j précédents : <span className={`font-bold ${forecast.trend_pct > 0 ? "text-primary" : forecast.trend_pct < 0 ? "text-destructive" : ""}`}>{forecast.trend_pct > 0 ? "+" : ""}{forecast.trend_pct} %</span>
          </div>
        </div>
      )}

      {/* Locked forecast teaser for non-Premium-AI */}
      {!isPremiumAI && (
        <div className="bg-card border border-border rounded-sm p-6 flex flex-col justify-between md:col-span-2" data-testid="demand-forecast-locked">
          <div>
            <div className="inline-flex items-center gap-1.5 bg-secondary/15 text-secondary text-[10px] uppercase tracking-[0.18em] font-bold px-2.5 py-1 rounded-sm mb-3">
              <Crown size={10} weight="fill"/> Premium IA
            </div>
            <div className="font-display text-lg font-medium mb-1">Prévision de demande 7 jours</div>
            <p className="text-sm text-muted-foreground">Chaque matin, sachez à quoi vous attendre : jours forts/creux, tendance vs mois dernier, et 1 action IA ciblée par jour.</p>
          </div>
          <a href="/passez-partenaire" className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-secondary">
            Passer Premium IA <Sparkle size={12} weight="fill"/>
          </a>
        </div>
      )}
    </div>
  );
}
